import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
export const frontmatter = {
  "title": "From Tokens to Thinking Systems: Making AI-Native Design Systems Actually Work",
  "slug": "from-tokens-to-thinking-systems-making-ai-native-design-systems-actually-work",
  "engagement": "ai-ready-ops",
  "publishedAt": "2025-12-29T08:16:00.000Z",
  "readTime": "9 min read",
  "excerpt": "A practical blueprint for AI-native design systems that LLMs can actually execute: tokens, schema and generative rules as explicit contracts.",
  "seoTitle": "From Tokens to Thinking Systems: AI-Native Design Systems",
  "authorName": "Petri Lahdelma",
  "authorSlug": "petri-lahdelma",
  "mainImageUrl": "https://cdn.sanity.io/images/ai4cwr0g/digitaltableteur-blog/6b129891b9e49f23f12d17b7822f66ea172548d1-900x512.png?w=1600"
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    blockquote: "blockquote",
    code: "code",
    figure: "figure",
    h2: "h2",
    h3: "h3",
    hr: "hr",
    li: "li",
    p: "p",
    pre: "pre",
    span: "span",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.p, {
      children: "In AI Design Systems: Why Tokens, Schema & Generative Rules Matter Now, Rythm lays out a clear and timely thesis:"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "Design systems are no longer built for designers. They are built for AI."
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "I strongly agree with that direction and I would argue we are now past the theory phase. The interesting failures I see in client work and in open experiments are not philosophical. They show up as the same diff, again and again: a component that looks fine in Storybook, reads well in Notion and still produces the wrong DOM, the wrong focus order or the wrong token when an agent assembles a screen from a one-line prompt."
    }), "\n", _jsx(_components.p, {
      children: "The real question in 2026 is no longer why tokens, schema and generative rules matter, but how we make them usable by real LLMs today, without hand-holding or tribal knowledge. That is the gap I have been trying to close with schema templates you can hand to a model and expect a repeatable result, not a lucky Friday afternoon."
    }), "\n", _jsx(_components.p, {
      children: "This article is a response and a concrete extension to Rythm's framing. It stays focused on one thing: LLM-consumable design systems that can actually generate UI, not just talk about it."
    }), "\n", _jsx(_components.h2, {
      children: "The Missing Piece: AI Needs Contracts, Not Concepts"
    }), "\n", _jsx(_components.p, {
      children: "Rythm correctly identifies the three pillars of AI-ready design systems: tokens, schema and generative rules. Tokens carry visual and semantic intent. Schema names what a component is allowed to be. Generative rules connect context (variant, mode, density, domain) to the token overrides that should apply. Together they are supposed to replace the designer's implicit judgment with something a model can execute."
    }), "\n", _jsx(_components.p, {
      children: "Most teams already have the first two in some form. Figma variables, Style Dictionary exports, Storybook props. The third pillar is where documentation usually stays fuzzy. A slide says \"primary uses brand accent in light mode,\" and everyone nods. An LLM does not nod. It needs a rule object with inputs, conditions and outputs."
    }), "\n", _jsx(_components.figure, {
      "data-rehype-pretty-code-figure": "",
      children: _jsx(_components.pre, {
        tabIndex: "0",
        "data-language": "json",
        "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
        children: _jsxs(_components.code, {
          "data-language": "json",
          "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
          style: {
            display: "grid"
          },
          children: [_jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"generativeRules\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": {"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"contextInputs\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"variant\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"mode\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"density\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "],"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"rules\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": ["
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "    { "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "\"if\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": { "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "\"variant\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"primary\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " }, "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "\"then\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": { "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "\"tokens\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": { "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "\"color\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"brand.accent\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " } } },"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "    { "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "\"if\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": { "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "\"variant\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"destructive\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " }, "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "\"then\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": { "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "\"tokens\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": { "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "\"color\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"semantic.error\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " } } },"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "    { "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "\"if\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": { "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "\"mode\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"dark\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " }, "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "\"then\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": { "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "\"tokens\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": { "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "\"surface\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"neutral.surfaceElevated\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " } } }"
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "  ]"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "}"
            })
          })]
        })
      })
    }), "\n", _jsx(_components.p, {
      children: "But there is an implicit assumption hiding in most discussions: if we define these things clearly enough, AI will understand them. In practice, that is not how LLMs work. They do not infer intent from prose, diagrams or Figma pages the way a senior designer does after three years on the same product. They operate best when given explicit, machine-readable contracts: deterministic structure, declared constraints, named responsibilities and predictable output shapes. In other words, schemas that leave very little room for interpretation."
    }), "\n", _jsx(_components.p, {
      children: "Behavior and interaction deserve the same treatment as color. A paragraph in the docs that says \"accordion panels close when another opens\" is not enough. The contract should state the default state, the state model (controlled vs uncontrolled) and what renders when."
    }), "\n", _jsx(_components.figure, {
      "data-rehype-pretty-code-figure": "",
      children: _jsx(_components.pre, {
        tabIndex: "0",
        "data-language": "json",
        "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
        children: _jsxs(_components.code, {
          "data-language": "json",
          "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
          style: {
            display: "grid"
          },
          children: [_jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"behavior\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": {"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"map\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"Clicking a trigger opens a panel and closes others.\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"stateModel\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"uncontrolled\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"rendering\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"Panels render only when open.\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"defaultState\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"all closed\""
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "},"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"interaction\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": {"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"logic\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": ["
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "    { "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "\"trigger\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"onClick\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "\"effect\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"toggle panel\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " }"
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "  ],"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"feedback\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": {"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "    \"hover\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"highlight trigger\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "    \"focus\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"focus ring\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "    \"active\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"press feedback\""
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "  }"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "}"
            })
          })]
        })
      })
    }), "\n", _jsxs(_components.p, {
      children: ["Accessibility is the same story. WCAG checklists in a wiki do not survive contact with a generative prompt. Roles, relationships and keyboard paths need to be part of the schema so the model cannot \"helpfully\" invent a ", _jsx(_components.code, {
        children: "div"
      }), " with a click handler where a button belongs."]
    }), "\n", _jsx(_components.figure, {
      "data-rehype-pretty-code-figure": "",
      children: _jsx(_components.pre, {
        tabIndex: "0",
        "data-language": "json",
        "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
        children: _jsxs(_components.code, {
          "data-language": "json",
          "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
          style: {
            display: "grid"
          },
          children: [_jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"accessibility\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": {"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"roles\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": {"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "    \"container\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"div\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "    \"primary\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"button\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "    \"panel\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"region\""
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "  },"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"aria\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": {"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "    \"expanded\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"true when open\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "    \"controls\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"panel-id\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "    \"labelledBy\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"trigger-id\""
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "  },"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"keyboard\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"Enter/Space toggles panel\""
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "}"
            })
          })]
        })
      })
    }), "\n", _jsx(_components.p, {
      children: "Without that level of explicitness, AI-generated UI quickly becomes visually plausible and structurally wrong. You get inconsistent spacing across runs, focus traps that only show up in production and brand drift that no linter catches because the output never touched your token files. That is not a model quality problem. It is a contract problem."
    }), "\n", _jsx(_components.h2, {
      children: "The Shift I am Proposing: From Design Systems to Generation Systems"
    }), "\n", _jsx(_components.p, {
      children: "Traditional design systems answer a human question: how should a person assemble UI from approved parts? The documentation, the Figma library and the Storybook stories all assume someone is making judgment calls in the loop. AI-native design systems have to answer a different question: under what conditions is UI allowed to exist and how should it be derived?"
    }), "\n", _jsx(_components.p, {
      children: "That shift changes what you document. Components stop being static artifacts you copy from a gallery. Variants stop being endless enumerations in a dropdown. Documentation stops being explanation that only makes sense after you have already built the thing. Instead you describe components as semantic objects (what they mean, where they may appear), behavior as logic that can be evaluated (not bullet points) and screens as outcomes of rules plus context."
    }), "\n", _jsx(_components.p, {
      children: "The goal is not to remove designers. It is to move their work up a layer: from placing instances to defining the system that places instances correctly when asked."
    }), "\n", _jsx(_components.h2, {
      children: "Two LLM Template Schemas (Public, Opinionated, Practical)"
    }), "\n", _jsx(_components.p, {
      children: "To explore this paradigm, I have prepared two publicly shared LLM template schemas designed explicitly for design-system autogeneration. They are not tied to a specific tool, vendor or framework, though I have also published a Tailwind-oriented variant for teams that live in utility classes. They are meant to work with any capable LLM, as long as you treat the JSON as source of truth and the prose around it as commentary, not the other way around."
    }), "\n", _jsx(_components.h3, {
      children: "1. Design System Semantic Schema"
    }), "\n", _jsx(_components.p, {
      children: "This schema answers one core question: what is this thing, really? It defines components as semantic entities, not as rectangles with padding. At a high level it encodes identity and purpose, allowed contexts, required vs optional properties, interaction states, accessibility obligations and containment rules (what may nest inside what)."
    }), "\n", _jsx(_components.p, {
      children: "A button in this model is not \"a clickable rectangle.\" It is an action trigger with intent (primary, destructive, passive), behavioral guarantees (what happens on press, loading, disabled), accessibility constraints (name, role, keyboard) and layout implications (inline vs block, icon placement). That gives the model comprehension, not just a class string that happened to match last time."
    }), "\n", _jsx(_components.h3, {
      children: "2. Generative Rule and Token Resolution Schema"
    }), "\n", _jsx(_components.p, {
      children: "The second schema answers a different question: given a context, how should this UI adapt? It formalizes token resolution logic, conditional overrides, environmental triggers (mode, density, accessibility preferences, viewport), precedence when two rules conflict and safe mutation boundaries (what an agent may never override without a human flag)."
    }), "\n", _jsx(_components.p, {
      children: "Instead of hardcoding twelve navigation variants, you describe evaluations: if accessibility mode favors low vision, adjust typography and contrast tokens; if viewport width drops below a threshold, switch navigation pattern; if the surface is financial data, prioritize numerical hierarchy tokens. UI becomes adaptive by default, not adaptive by exception after someone files a bug."
    }), "\n", _jsx(_components.h2, {
      children: "Why This Matters (And Where Many Systems Fail)"
    }), "\n", _jsx(_components.p, {
      children: "Most AI design system experiments I have reviewed fail for a simple reason. They describe systems. They do not define execution logic. The Notion page is beautiful. The Figma file is meticulous. The prompt still hallucinates a secondary button style because nothing in machine-readable form said it could not."
    }), "\n", _jsx(_components.p, {
      children: "LLMs are extremely good at following rules, but only if those rules are explicit, scoped and validated. When you separate semantic meaning, token intent and generative behavior into distinct layers, you give the model just enough structure to produce UI that stays consistent, accessible, brand-aligned, evolvable and auditable. Auditable matters: if a screen is wrong, you want to trace which rule fired, not re-run the prompt until luck improves."
    }), "\n", _jsx(_components.h2, {
      children: "This Is Meta-Design, Not Automation"
    }), "\n", _jsx(_components.p, {
      children: "Rythm frames this shift well when he writes that your job moves from designing screens to designing the logic that generates screens. These schemas are my attempt to make that shift operational, not aspirational."
    }), "\n", _jsx(_components.p, {
      children: "They do not replace designers. They raise the abstraction level of design work. You spend more time defining intent, encoding judgment, shaping constraints and anticipating misuse (what happens when someone asks for a destructive primary button on a checkout step). The model's job is execution inside those boundaries. AI does not design in the sense of owning tradeoffs; it runs the system you designed."
    }), "\n", _jsx(_components.h2, {
      children: "Where This Is Going Next"
    }), "\n", _jsx(_components.p, {
      children: "I expect the next wave to look less like \"chat to UI\" and more like schema-driven UI APIs, token-only brand definitions, AI-assisted design QA in CI, runtime-adaptive interfaces and workflows where Figma artifacts are outputs of the system rather than the source of truth. None of that works unless the contracts are right first. That is what these public schemas are for: a place to stress-test the idea with real prompts, real products and real criticism."
    }), "\n", _jsx(_components.h2, {
      children: "Validation checklist"
    }), "\n", _jsx(_components.p, {
      children: "Before you trust generated UI in a pipeline, walk through this list against your own components. It is deliberately boring. Boring is what keeps agents from improvising."
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Schema uses required sections with meaningful content."
      }), "\n", _jsx(_components.li, {
        children: "Behavior and interaction maps match actual component behavior."
      }), "\n", _jsx(_components.li, {
        children: "Constraints align with real layout limitations."
      }), "\n", _jsx(_components.li, {
        children: "Accessibility roles and keyboard rules are accurate."
      }), "\n", _jsx(_components.li, {
        children: "Generative rules map to tokens or Tailwind classes correctly."
      }), "\n", _jsx(_components.li, {
        children: "Storybook exposes schema for review and auditing."
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      children: "Open Invitation: Tear This Apart"
    }), "\n", _jsx(_components.p, {
      children: "These schemas are public by design."
    }), "\n", _jsx(_components.p, {
      children: "They are:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Opinionated"
      }), "\n", _jsx(_components.li, {
        children: "Incomplete"
      }), "\n", _jsx(_components.li, {
        children: "Evolving"
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "And that is intentional."
    }), "\n", _jsx(_components.p, {
      children: "I would rather ship something wrong in the open and fix it with your production scars than polish a PDF nobody runs against GPT-4o on a Monday morning."
    }), "\n", _jsx(_components.p, {
      children: "If you are working on:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "AI-assisted design systems"
      }), "\n", _jsx(_components.li, {
        children: "Design tokens at scale"
      }), "\n", _jsx(_components.li, {
        children: "Schema-first UI"
      }), "\n", _jsx(_components.li, {
        children: "Generative interfaces"
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "I would genuinely love your critique."
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "What is missing?"
      }), "\n", _jsx(_components.li, {
        children: "What is naive?"
      }), "\n", _jsx(_components.li, {
        children: "What breaks under real product pressure?"
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "I have linked the schemas below. Comments, suggestions and brutal feedback are very welcome."
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.a, {
        href: "https://petrilahdelma.gumroad.com",
        children: "LLM Component Schema Template (Gumroad)"
      })
    }), "\n", _jsx(_components.p, {
      children: "PS. I promise to follow up with an updated version if I get good suggestions or comments!"
    }), "\n", _jsx(_components.p, {
      children: "LLM Component Schema Guide. MIT Distribution License. Copyright Petri Lahdelma Digitaltableteur. digitaltableteur.com"
    }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.p, {
      children: "Build systems, not screens, but make sure your systems are precise enough that AI cannot misunderstand them."
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
